import React, { useState } from 'react';
import { useRepositoryData } from './hooks/useRepositoryData';
import Header from './components/Header';
import TabBar from './components/TabBar';
import CharacterTab from './components/tabs/CharacterTab';
import EntriesTab from './components/tabs/EntriesTab';
import RecapTab from './components/tabs/RecapTab';
import PromptsTab from './components/tabs/PromptsTab';
import InspirationTab from './components/tabs/InspirationTab';
import CharacterModal from './components/modals/CharacterModal';
import EntryModal from './components/modals/EntryModal';

const TABS = [
  { id: 'characters', label: 'Characters', icon: '🎭' },
  { id: 'recap', label: 'Session Recaps', icon: '📝' },
  { id: 'prompts', label: 'Prompts', icon: '💬' },
  { id: 'inspiration', label: 'Inspiration', icon: '🖼️' },
  { id: 'all', label: 'All', icon: '📖' },
  { id: 'kith', label: 'Custom Kith', icon: '🌿' },
  { id: 'place', label: 'Places', icon: '🗺️' },
  { id: 'session', label: 'Journey Notes', icon: '🕯️' },
  { id: 'rule', label: 'House Rules', icon: '📜' },
];

export default function App() {
  const { entries, characters, recaps, keepsakes, loading, refresh } = useRepositoryData();
  const [activeTab, setActiveTab] = useState('characters');
  
  const [isCharModalOpen, setIsCharModalOpen] = useState(false);
  const [editingChar, setEditingChar] = useState(null);
  
  const [isEntryModalOpen, setIsEntryModalOpen] = useState(false);
  const [entryType, setEntryType] = useState('kith');
  const [editingEntry, setEditingEntry] = useState(null);

  const openCharModal = (char = null) => {
    setEditingChar(char);
    setIsCharModalOpen(true);
  };

  const openEntryModal = () => {
    const type = ['kith', 'place', 'session', 'rule'].includes(activeTab) ? activeTab : 'kith';
    setEntryType(type);
    setEditingEntry(null);
    setIsEntryModalOpen(true);
  };

  const openEntryEditModal = (entry) => {
    setEditingEntry(entry);
    setEntryType(entry.type);
    setIsEntryModalOpen(true);
  };

  const closeEntryModal = () => {
    setIsEntryModalOpen(false);
    setEditingEntry(null);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="text-center p-4 font-lora italic text-theme-honey bg-theme-honey/10 rounded ml-4 mb-4">
          Loading from the repository…
        </div>
      );
    }
    
    if (activeTab === 'characters') return <CharacterTab characters={characters} keepsakes={keepsakes} onOpenModal={openCharModal} onRefresh={refresh} />;
    if (activeTab === 'recap') return <RecapTab recaps={recaps} onRefresh={refresh} />;
    if (activeTab === 'prompts') return <PromptsTab />;
    if (activeTab === 'inspiration') return <InspirationTab />;
    
    return <EntriesTab entries={entries} activeTab={activeTab} onOpenModal={openEntryModal} onEditEntry={openEntryEditModal} onRefresh={refresh} />;
  };

  return (
    <div className="max-w-[1140px] mx-auto px-6 pt-6 pb-16">
      <Header />
      <TabBar tabs={TABS} activeTab={activeTab} setActiveTab={setActiveTab} entries={entries} characters={characters} />
      
      <div className="bg-parchment border-[1.5px] border-parchment-dark rounded-b-md rounded-tr-md min-h-[500px] p-8 pb-12 shadow-parchment content-panel-bg">
        {renderContent()}
      </div>

      <div className="text-center mt-10 font-lora text-sm italic text-ink-light">
        Greta's wandering companions ✦ {characters.length} characters · {entries.length} entries
      </div>

      {isCharModalOpen && (
        <CharacterModal 
          onClose={() => setIsCharModalOpen(false)} 
          existingChar={editingChar} 
          onSaved={refresh}
        />
      )}
      {isEntryModalOpen && (
        <EntryModal 
          onClose={closeEntryModal} 
          initialType={entryType} 
          existingEntry={editingEntry}
          onSaved={refresh}
        />
      )}
    </div>
  );
}